/**
 * New Ticket view — form for creating a support ticket from plant or site context.
 *
 * Exported in two flavors: NewTicketViewPlant (plant-context route, no site,
 * standalone chrome) and NewTicketViewSite (site-context route, pre-filled
 * site — nested under SiteDashboardLayout, which supplies AppNav + TitleBar
 * chrome for it). Both share the same form stage component; the HOC data
 * layer has been replaced with useSiteGroup + useCustomer hooks (plant) /
 * SiteDashboardLayout's context (site).
 *
 * Contains:
 * - NewTicketFormStage — shared submit + form logic (no chrome)
 * - NewTicketViewPlant — plant-context route component
 * - NewTicketViewSite  — site-context route child component
 */
import { Box } from '@mui/material';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';

import AppNav from '@sophocles/components/App/AppNav';
import TitleBar from '@sophocles/components/App/TitleBar';
import LoadingScreen from '@sophocles/components/LoadingScreen';
import { useCreateTicketMutation } from '@sophocles/features/entities/tickets';
import { addToast } from '@sophocles/features/toast';
import useCustomer from '@sophocles/hooks/useCustomer';
import useSiteGroup from '@sophocles/hooks/useSiteGroup';
import { parseApiError } from '@sophocles/utils/apiErrors';
import { useSiteDashboardLayoutContext } from '@sophocles/views/sites/SiteDashboardLayout';

import NewTicketForm from './forms/NewTicketForm';
import { buildRmaHandoff } from './rmaHandoff';

/**
 * Shared submit + form stage for both plant and site ticket-creation routes.
 * Renders no chrome — callers wrap it in whatever AppNav/TitleBar the route
 * needs (NewTicketViewPlant renders its own; NewTicketViewSite relies on
 * SiteDashboardLayout's).
 *
 * @param {{ customer: import('immutable').Map, selectedPlant: import('immutable').Map, selectedStructure: import('immutable').Map | null }} props
 */
function NewTicketFormStage({ customer, selectedPlant, selectedStructure }) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [createTicket] = useCreateTicketMutation();
  const [nonFieldMessages, setNonFieldMessages] = React.useState([]);
  const plainSiteGroup = selectedPlant ? selectedPlant.toJS() : null;

  const handleSubmit = async (values, { createRMA, selectedContact }) => {
    const payload = {
      ...values,
      new_contact_id: selectedContact?.id ?? null,
      contact_id: null,
      customer_id: customer.get('id', 0),
      site_group_id: selectedPlant ? selectedPlant.get('id', null) : null,
      site_id: selectedStructure ? selectedStructure.get('id', null) : null,
      severity: 10,
    };

    setNonFieldMessages([]);
    const result = await createTicket({ body: payload });

    if (result.error) {
      setNonFieldMessages(
        parseApiError(result.error, {
          knownFields: [],
          genericMessage: t('tickets.ticketCreateFailed', 'Failed to create ticket. Please try again.'),
        }).nonFieldMessages,
      );
      return;
    }

    const ticket = result.data;
    dispatch(
      addToast({
        title: t('general.success', 'Success'),
        message: t('tickets.ticketCreated', 'Ticket Created'),
        level: 'success',
        dismissible: true,
      }),
    );

    const ticketViewPath = `/support/ticket/${ticket?.id ?? 0}/view`;
    if (!createRMA) {
      navigate(ticketViewPath, { replace: true });
      return;
    }

    // The ticket exists by now; a hand-off failure must never look like a
    // silent no-op, so it lands the user on the ticket with a visible error.
    try {
      navigate('/rma/new', {
        replace: true,
        state: {
          fromTicket: buildRmaHandoff({
            customer,
            siteGroup: selectedPlant,
            site: selectedStructure,
            ticket,
            selectedContact,
            payload,
          }),
        },
      });
    } catch (error) {
      console.error('RMA hand-off failed after the ticket was created', error);
      dispatch(
        addToast({
          title: t('general.error', 'Error'),
          message: t(
            'tickets.rmaHandoffFailed',
            'Ticket saved, but the RMA form could not be opened. Create the RMA from the RMA list.',
          ),
          level: 'error',
          dismissible: true,
        }),
      );
      navigate(ticketViewPath, { replace: true });
    }
  };

  return (
    <NewTicketForm
      customer={customer}
      selectedPlant={plainSiteGroup}
      selectedStructure={selectedStructure}
      onSubmit={handleSubmit}
      onCancel={() => navigate(-1)}
      errorMessages={nonFieldMessages}
    />
  );
}

/**
 * Plant-context route: /c/:cust_id/plant/:sitegroup_id/ticket/new
 * No site_id in the URL — selectedStructure is null/empty. Standalone route
 * (not nested under SiteDashboardLayout) — renders its own AppNav + TitleBar.
 */
export function NewTicketViewPlant(props) {
  const { t } = useTranslation();
  const { cust_id, sitegroup_id } = useParams();
  const { customer, loading: customerLoading } = useCustomer(Number(cust_id));
  const { siteGroup, loading: siteGroupLoading } = useSiteGroup(Number(sitegroup_id));

  const loading = customerLoading || siteGroupLoading;

  if (loading) {
    return <LoadingScreen />;
  }

  return (
    <AppNav notFullHeight loading={loading} page="plant_tickets" {...props}>
      <Box sx={{ padding: '0 var(--page-px) var(--page-py)' }}>
        <TitleBar showBackButton title={t('support.newTicket', 'New Ticket')} />
        <NewTicketFormStage customer={customer} selectedPlant={siteGroup} selectedStructure={null} />
      </Box>
    </AppNav>
  );
}

/**
 * Site-context route child: nested under SiteDashboardLayout at
 * .../structure/:site_id/ticket/new. Full site context — selectedStructure
 * is the joined site entity. Renders only the form stage; the layout
 * supplies AppNav + TitleBar chrome for this subroute.
 */
export function NewTicketViewSite() {
  const { customer, selectedStructure, selectedPlant, loading } = useSiteDashboardLayoutContext();

  if (loading) {
    return <LoadingScreen />;
  }

  return <NewTicketFormStage customer={customer} selectedPlant={selectedPlant} selectedStructure={selectedStructure} />;
}

export default NewTicketViewPlant;
